import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import { can } from '../permissions';
import { useEscapeToClose, useFocusTrap } from '../motion';
import {
  INVOICE_STAGES,
  INVOICE_STATUS_WORD,
  INVOICE_STATUS_TONE,
  stageIndex,
  duplicateLineIds,
  canMarkPaid,
  buildDeliveryEvidence,
  lineDeliveryBasis,
  deliveredShare,
  deliveryNudgeTrips,
  retentionCeiling,
  retentionPayBlock,
  lineBaseAmount,
  pendingCorrection,
  payStampRef,
  payStampKind,
  payStampState,
  civilDay,
  paymentValueDateIssue,
  type InvoiceLine,
  type InvoiceStatus,
  type DeliveryBasis,
} from '../projects';
import { milestoneClaim } from '../milestones';
import { fmtMoney, fmtIn, asAmount } from '../money';
import { displayName } from '../people';
import { MONTHS } from '../when';
import DateField from './DateField';
import Icon from './Icon';
import { CorrectionDialog } from './CorrectionDialog';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import PaymentChainPanel, { buildChainView } from './PaymentChainPanel';
import { policyForProject } from '../payment-approval';

/*
 * THE INVOICE-LINE DRAWER — the row detail of the budget ledger, re-hung as a
 * right-docked slide-over (invoice-acceptance governance). Everything the busy
 * row used to try to carry lives here: the five-node gate, the attached invoice
 * and its typed fields, the PO-vs-invoice variance, the frozen delivery evidence,
 * the duplicate warning, the segregation-of-duties caveat and the single next
 * action. Built on the app's own modal frame — no new visual language.
 *
 * PDF auto-fill of the fields is a LATER increment, so in THIS increment Amount /
 * Vendor / Invoice date / Invoice ref are TYPED by the person (no "read from PDF"
 * ticks are faked). The gate advances one step at a time; Mark paid is locked
 * until the line has been BOTH accepted and approved (enforced in the store).
 */

/** A stored timestamp (ms) → '4 Sept 2026'. */
function fmtWhen(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
/** 'YYYY-MM-DD' → '21 Sept 2026'. */
function fmtDay(day?: string): string {
  if (!day) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const mon = MONTHS[Number(m[2]) - 1];
  return mon ? `${Number(m[3])} ${mon} ${m[1]}` : day;
}

/**
 * SET OR CORRECT THE FINANCE PAYMENT DATE (#481). A paid line keeps its immutable
 * "marked paid in FlightDeck" moment; this records the real value date from the
 * finance system. Changing an already-set date needs a reason; a future date is
 * allowed but warned; a date before the approval is held back by the field's `min`
 * and refused by the store.
 */
function PaymentDateDialog({ line, onSave, onClose }: {
  line: InvoiceLine;
  onSave: (value: string, reason: string) => Promise<string>;
  onClose: () => void;
}) {
  const inert = useSampleInert();
  const correcting = line.paymentValueDate != null;
  // A payment can't have cleared before the work was approved — that is the floor.
  // If a paid line is missing its approval stamp (a data anomaly), fall back to when
  // the invoice was received, then to its committed date, so the field is never
  // left wide open to an absurd past date.
  const minDay = line.approvedAt != null ? civilDay(line.approvedAt)
    : line.receivedAt != null ? civilDay(line.receivedAt)
    : (line.date || undefined);
  const [value, setValue] = useState(line.paymentValueDate ?? '');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // The box holds an entry it could not accept (unreadable or out of range). While
  // it does, the shown value, its own error and the value a Save would write would
  // disagree — so Save is held and the dialog's own warnings are suppressed, and
  // the field's error is the one source of truth (#481-B).
  const [badDate, setBadDate] = useState(false);
  const v = value.trim();
  const issue = v ? paymentValueDateIssue(line, v, civilDay(Date.now())) : null;
  const changed = v !== (line.paymentValueDate ?? '');
  const canSave = !badDate && !!v && issue !== 'before-approval' && changed && (!correcting || reason.trim().length > 0) && !busy;
  const save = async () => {
    if (!canSave) return;   // never write a value the box no longer shows (#481-B)
    setBusy(true); setErr('');
    const e = await onSave(v, reason.trim());
    setBusy(false);
    if (e) setErr(e); else onClose();
  };
  // Escape closes only this dialog, never the invoice drawer behind it (which
  // would discard what was typed). Capture phase + stopPropagation beats the
  // drawer's own window Escape (app-wide nested-overlay rule, as in CorrectionDialog).
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [onClose]);
  return createPortal(
    <div className="overlay" onClick={onClose}>
      <div className="modal id-reason" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-head-title">{correcting ? 'Correct the finance payment date' : 'Payment date (per finance)'}</span>
        </div>
        <div className="modal-body">
          <p className="id-reason-p">The date your finance system shows the payment cleared. It keeps the original “marked paid in FlightDeck” moment unchanged, and never changes the authority to pay.</p>
          <label className="af-cell">
            <span className="af-l">Payment date<span className="req">*</span></span>
            <DateField ariaLabel="Payment date per finance" value={value} variant="form" autoFocus min={minDay} onChange={setValue} onInvalidChange={setBadDate} />
          </label>
          {issue === 'future' && !badDate && (
            <p className="id-reason-p pvd-warn"><Icon name="warning" size={13} /> That date is in the future — the payment hasn’t cleared yet. You can still save it.</p>
          )}
          {correcting && (
            <label className="af-cell">
              <span className="af-l">Why the change?<span className="req">*</span></span>
              <textarea className="input" rows={2} value={reason} aria-required
                placeholder="e.g. finance confirmed the true value date"
                onChange={(e) => setReason(e.target.value)} />
            </label>
          )}
          {err && <p className="id-problem">{err}</p>}
        </div>
        <div className="modal-actions id-reason-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button disabled={!canSave} {...inert('btn btn-primary btn-small', () => void save())}>
            {busy ? 'Saving…' : correcting ? 'Save correction' : 'Save finance date'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function StatusPill({ status }: { status: InvoiceStatus }) {
  return (
    <span className={`st st-${INVOICE_STATUS_TONE[status]}`}>
      <span className="st-dot" />
      {INVOICE_STATUS_WORD[status]}
    </span>
  );
}

/** The five-node gate. Steps up to `doneUpTo` are cleared (green tick); the next
 *  one is the amber ring; the rest are waiting. */
function Stepper({ doneUpTo }: { doneUpTo: number }) {
  return (
    <ol className="gsteps">
      {INVOICE_STAGES.map((label, i) => {
        const cls = i <= doneUpTo ? 'done' : i === doneUpTo + 1 ? 'next' : 'todo';
        const leg = i <= doneUpTo ? ' leg-done' : '';
        return (
          <li key={label} className={`gs ${cls}${leg}`}>
            <span className="gs-dot">{cls === 'done' && <Icon name="check" size={11} />}</span>
            <span className="gs-lbl">{INVOICE_STATUS_WORD[label]}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function InvoiceDrawer({
  projectId,
  line,
  leaving,
  onClose,
}: {
  projectId: string;
  line: InvoiceLine;
  leaving: boolean;
  onClose: () => void;
}) {
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const role = useApp((s) => s.shownRole);
  const mayPlan = can(role, 'editSettings');
  const sample = useSampleBoard();
  const inert = useSampleInert();
  // Configuring WHO approves payments is admin-only (the workspace-wide chain) — a
  // manager may authorise a payment but not set the approvers (segregation of duties).
  const maySetApprovers = can(role, 'manageWorkspace');
  const base = useApp((s) => s.currency);
  const fxRates = useApp((s) => s.fxRates);
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const issues = useApp((s) => s.issues);
  const profiles = useApp((s) => s.profiles);
  const updateBudgetLine = useApp((s) => s.updateBudgetLine);
  const receiveBudgetLine = useApp((s) => s.receiveBudgetLine);
  const acceptBudgetLine = useApp((s) => s.acceptBudgetLine);
  const approveBudgetLine = useApp((s) => s.approveBudgetLine);
  const requestPayApproval = useApp((s) => s.requestPayApproval);
  const payApprovalFor = useApp((s) => s.payApprovalFor);
  const payBudgetLine = useApp((s) => s.payBudgetLine);
  // The payment-approval chain (executive-approver tier).
  const paymentApproval = useApp((s) => s.paymentApproval);
  const raisePaymentChain = useApp((s) => s.raisePaymentChain);
  const authorisePaymentStage = useApp((s) => s.authorisePaymentStage);
  const paymentAuthorityFor = useApp((s) => s.paymentAuthorityFor);
  const previewPaymentChain = useApp((s) => s.previewPaymentChain);
  const remindPaymentChain = useApp((s) => s.remindPaymentChain);
  const setView = useApp((s) => s.setView);
  const setPaymentValueDate = useApp((s) => s.setPaymentValueDate);
  const attachInvoiceFile = useApp((s) => s.attachInvoiceFile);
  // Re-read authority when the ledger, my cover, or my role changes.
  const signoffCover = useApp((s) => s.signoffCover);
  const decideCorrection = useApp((s) => s.decideCorrection);
  const setBilledAgainst = useApp((s) => s.setBilledAgainst);
  const setDeliveryBasis = useApp((s) => s.setDeliveryBasis);
  const meEmail = useApp((s) => s.user?.email ?? '').toLowerCase();
  const budgetLineFileUrl = useApp((s) => s.budgetLineFileUrl);
  const removeBudgetLine = useApp((s) => s.removeBudgetLine);
  const authorityToPayUrl = useApp((s) => s.authorityToPayUrl);

  const drawerRef = useRef<HTMLElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEscapeToClose(onClose, !leaving);

  const [problem, setProblem] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [pvdOpen, setPvdOpen] = useState(false);

  // The typed invoice fields — seeded from the line, committed on blur. A blank
  // amount that will not parse is held locally rather than written.
  const [amount, setAmount] = useState(String(line.amount ?? ''));
  const [vendor, setVendor] = useState(line.vendor ?? '');
  const [invDate, setInvDate] = useState(line.date ?? '');
  const [invRef, setInvRef] = useState(line.invoiceRef ?? '');
  const [dueDate, setDueDate] = useState(line.dueDate ?? '');
  // Re-seed when a different line is opened into the same drawer.
  useEffect(() => {
    setAmount(String(line.amount ?? ''));
    setVendor(line.vendor ?? '');
    setInvDate(line.date ?? '');
    setInvRef(line.invoiceRef ?? '');
    setDueDate(line.dueDate ?? '');
    setProblem('');
    setConfirmRemove(false);
    setPvdOpen(false);
  }, [line.id]);

  const idx = stageIndex(line.status);
  const settled = line.status === 'paid';
  const phaseName = (id?: string) => (id ? phases.find((p) => p.id === id)?.name : undefined);
  const isForeign = !!line.currency && line.currency !== base;
  const faceMoney = (n?: number) => (n == null ? '' : isForeign ? fmtIn(line.currency, n) : fmtMoney(n));

  const dupFlagged = useMemo(
    () => duplicateLineIds(project?.invoices ?? []).has(line.id),
    [project?.invoices, line.id],
  );

  // The delivery evidence: the frozen snapshot once accepted, otherwise a live
  // preview of the phase as it stands (captured onto the line when it is accepted).
  const evidence = useMemo(() => {
    if (line.acceptance) return { snap: line.acceptance, frozen: true };
    const basis = lineDeliveryBasis(line);
    if (!basis) return null;
    const snap = buildDeliveryEvidence(
      basis,
      {
        cards: issues,
        phaseName: (pid) => phases.find((p) => p.id === pid)?.name,
        milestones: milestones.map((m) => ({ key: m.id, name: m.name, actualDay: m.actualDay ?? undefined })),
      },
      Date.now(),
    );
    return snap ? { snap, frozen: false } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.acceptance, line.deliveryBasis, line.phase, issues, phases, milestones]);

  // THE DELIVERY BASIS — what this line's delivery is measured against (#483): a
  // phase, an epic, or a milestone. Editable (by a planner) until the line is
  // accepted, when the evidence above is frozen. The epic list is the board's own
  // themes; phases and milestones come from state.
  const epicList = useMemo(
    () => [...new Set(issues.map((i) => (i.epic ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [issues],
  );
  const livePhases = phases.filter((p) => !p.retiredAt);
  const curBasis = lineDeliveryBasis(line);
  const curBasisValue = curBasis ? `${curBasis.kind}::${curBasis.id}` : '';
  const basisReadLabel = !curBasis
    ? 'Not tracked'
    : curBasis.kind === 'phase'
      ? phaseName(curBasis.id) ?? 'a phase'
      : curBasis.kind === 'epic'
        ? curBasis.id
        : milestones.find((m) => m.id === curBasis.id)?.name ?? 'a milestone';
  const pickDeliveryBasis = async (value: string) => {
    if (!project) return;
    setProblem('');
    let basis: DeliveryBasis | null = null;
    if (value) {
      const sep = value.indexOf('::');
      const kind = value.slice(0, sep);
      const id = value.slice(sep + 2);
      if ((kind === 'phase' || kind === 'epic' || kind === 'milestone') && id) basis = { kind, id };
    }
    const err = await setDeliveryBasis(project.id, line.id, basis);
    if (err) setProblem(err);
  };

  // THE DELIVERY NUDGE (#483) — the board's bar, this line's delivered share, and
  // whether approving now would fall below the bar. A WARNING only; it never blocks.
  const deliveryNudge = project?.deliveryNudge;
  const deliveredNow = evidence ? deliveredShare(evidence.snap) : 1;
  const nudgeWarns = line.status === 'accepted' && deliveryNudgeTrips(deliveryNudge, deliveredNow);
  const belowNote = line.approvedBelowDelivery;
  const pctOf = (share: number) => `${Math.round(share * 100)}%`;

  // THE RETENTION CEILING — for an approved line about to be paid, whether paying
  // it would push total paid past budget − retention. Drives the block + banner.
  const ceiling = useMemo(
    () => retentionCeiling(
      { invoices: project?.invoices ?? [], retentionRate: project?.retentionRate, budget: project?.budget },
      { base, rates: fxRates },
    ),
    [project, base, fxRates],
  );
  const lineBase = lineBaseAmount(line, base, fxRates);
  const payBlock = retentionPayBlock(ceiling, lineBase);
  const retentionBlocked = line.status === 'approved' && payBlock.blocked;

  const commit = (patch: Partial<Omit<InvoiceLine, 'id'>>) => {
    if (!project) return;
    void updateBudgetLine(project.id, line.id, patch);
  };
  const commitAmount = () => {
    const n = asAmount(amount);
    if (n == null) { setAmount(String(line.amount ?? '')); return; }
    if (n !== line.amount) commit({ amount: n });
  };

  const act = async (fn: () => Promise<string>) => {
    setProblem('');
    const err = await fn();
    if (err) setProblem(err);
  };
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !project) return;
    setProblem('');
    const err = await attachInvoiceFile(project.id, line.id, file);
    if (err) setProblem(err);
  };
  const viewInvoice = async () => {
    if (!project) return;
    const url = await budgetLineFileUrl(project.id, line.id);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ── THE AUTHORITY TO PAY — the finance-facing page, built on demand from the
  // approval facts and the invoice fingerprint (Deliverable 2). The store builds it
  // (shared with the finance queue) so it always reflects the current approval and
  // is bound to the invoice the line now carries; nothing stored can fall stale.
  const [makingAuthority, setMakingAuthority] = useState(false);
  const viewAuthority = async () => {
    if (!project || !line.approvedAt) return;
    setMakingAuthority(true);
    setProblem('');
    const url = await authorityToPayUrl(project.id, line.id);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } else {
      setProblem('The authority to pay could not be produced. Nothing was changed — try again.');
    }
    setMakingAuthority(false);
  };
  const remove = async () => {
    if (!project) return;
    setProblem('');
    const err = await removeBudgetLine(project.id, line.id);
    if (err) { setProblem(err); return; }
    onClose();
  };

  const context = [line.description, line.invoiceRef, line.poNumber].filter(Boolean).join(' · ');
  const poVariance =
    line.poNumber && line.poAmount != null && line.poAmount !== line.amount
      ? { po: line.poAmount, inv: line.amount, over: line.amount > line.poAmount }
      : null;

  // The single next action in the footer, by where the line stands. 'accepted' is
  // not here — approving for payment is authority-gated and drawn separately below.
  const NEXT: Partial<Record<InvoiceStatus, { label: string; run: () => Promise<string> }>> = {
    committed: { label: 'Mark received', run: () => receiveBudgetLine(project!.id, line.id) },
    received: { label: 'Accept work', run: () => acceptBudgetLine(project!.id, line.id) },
    approved: { label: 'Mark paid', run: () => payBudgetLine(project!.id, line.id) },
  };
  const next = NEXT[line.status];
  // The pay action is held back when the retention ceiling would refuse it.
  const nextDisabled = retentionBlocked;

  // ── APPROVAL AUTHORITY — invoice-approval governance, 2 Oct ───────────────────
  // Whether an approved line's figures are locked (read-only until it is re-opened).
  const financialsLocked = line.status === 'approved' || line.approvedAt != null;
  // Who may approve this line right now, and how. The same rule the store enforces.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const auth = useMemo(
    () => (project ? payApprovalFor(project.id, line.id) : null),
    [project?.id, line.id, line.status, line.acceptedBy, line.approver, project?.payApprover, signoffCover, role],
  );
  const hasInvoice = !!line.invoiceFile;
  const approver = (line.approver ?? project?.payApprover ?? '').trim();
  // The authority stamp on an approved line — its reference, how it was given, and
  // whether it still binds to the invoice on the line (invoice-approval governance).
  const isApproved = line.approvedAt != null;
  const stampRef = payStampRef(line);
  const stampKind = isApproved ? payStampKind(line) : 'approver';
  const stampState = payStampState(line);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const doApprove = () => {
    if (!project || !auth?.allowed) return;
    // A reason is required to stand in for the approver / override SoD, AND to
    // approve ahead of the board's delivery bar (#483) — open the reason modal.
    if (auth.needsReason || nudgeWarns) { setReasonText(''); setReasonOpen(true); return; }
    void act(() => approveBudgetLine(project.id, line.id));
  };
  const submitReason = () => {
    if (!project) return;
    const r = reasonText.trim();
    if (!r) return;
    setReasonOpen(false);
    void act(() => approveBudgetLine(project.id, line.id, r));
  };

  // ── THE PAYMENT-APPROVAL CHAIN (executive-approver tier) ──────────────────────
  // A line is governed by the CHAIN when one is already frozen onto it, or the
  // workspace has a chain configured. A board with neither still runs the legacy
  // single-approver path above; a configured-but-unusable chain blocks (first-run).
  const policy = useMemo(() => policyForProject(paymentApproval, projectId), [paymentApproval, projectId]);
  const payChain = line.payChain ?? null;
  const chainRaised = !!payChain && payChain.length > 0;
  const chainGoverns = chainRaised || !!policy;
  const chainAuth = chainRaised ? paymentAuthorityFor(projectId, line.id) : null;
  const preview = !chainRaised && policy ? previewPaymentChain(projectId, line.id) : null;
  const chainRows = chainRaised ? payChain! : preview?.rows ?? [];
  const chainProblem = chainRaised ? '' : preview?.problem ?? (policy ? '' : 'No payment approver is set.');
  const chainUsable = chainRows.length > 0 && !chainProblem;
  const chainView = buildChainView({
    rows: chainRows,
    stageLabel: (id) => policy?.stages.find((s) => s.id === id)?.label ?? 'Approval stage',
    acceptance: line.acceptedAt ? { by: line.acceptedBy, at: line.acceptedAt } : undefined,
    meEmail,
    acceptedBy: line.acceptedBy,
    auth: chainAuth,
    profiles,
    settled: chainRaised && isApproved,
  });
  const iAcceptedThis = !!line.acceptedBy && line.acceptedBy.toLowerCase() === meEmail;

  // Authorise the open stage — a reason is required only to override separation of
  // duties (an admin authorising work they verified), mirroring the legacy modal.
  const [authReasonOpen, setAuthReasonOpen] = useState(false);
  const [authReasonText, setAuthReasonText] = useState('');
  const doAuthorise = () => {
    if (!project || !chainAuth?.allowed) return;
    if (chainAuth.needsReason || chainAuth.sodOverride) { setAuthReasonText(''); setAuthReasonOpen(true); return; }
    void act(() => authorisePaymentStage(project.id, line.id, 'approved'));
  };
  const submitAuthReason = () => {
    if (!project) return;
    const r = authReasonText.trim();
    if (!r) return;
    setAuthReasonOpen(false);
    void act(() => authorisePaymentStage(project.id, line.id, 'approved', r));
  };
  const goToApprovalSetup = () => { setView('payapproval'); onClose(); };

  // The lead banner above the chain: the viewer's "you're asked to sign" cue, else
  // the plain "waiting on X" note. Only for a raised, in-flight chain.
  const chainLead = !chainUsable ? null : chainAuth?.allowed ? (
    <div className="pa-youseat">
      <Icon name="shield" size={18} />
      <span className="pa-youseat-b">
        {chainAuth.sodOverride ? (
          <>You verified this work. You can still authorise the <b>{chainView.waitsOn?.stageName}</b> stage as an admin — it&rsquo;s recorded as a control exception, with your reason.</>
        ) : chainView.waitsOn && chainView.waitsOn.need > 1 ? (
          <>You&rsquo;re asked to authorise the <b>{chainView.waitsOn.stageName}</b> stage. Your signature makes <b>{chainView.waitsOn.signed + 1} of {chainView.waitsOn.need}</b>.</>
        ) : (
          <>You&rsquo;re asked to authorise the <b>{chainView.waitsOn?.stageName}</b> stage — yours clears it.</>
        )}
      </span>
    </div>
  ) : chainRaised && chainView.waitsOn && chainView.waitsOn.names.length ? (
    <div className="pa-wait">
      <Icon name="clock" size={18} />
      <span className="pa-wait-b">
        Waiting on <b>{chainView.waitsOn.names.join(' and ')}</b> to authorise the <b>{chainView.waitsOn.stageName}</b> stage.
        {chainView.waitsOn.need > 1 ? <> <b>{chainView.waitsOn.signed} of {chainView.waitsOn.need}</b> required signatures in.</> : null}
      </span>
    </div>
  ) : null;

  // ── CORRECTIONS — change the capex/opex pool or the phase, governed ────────────
  const [correcting, setCorrecting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // The drawer traps focus — but the approve-reason dialog, the Reclassify dialog
  // AND the payment-date dialog are portalled to the body (outside the drawer), so
  // the trap must YIELD while any of them is open or it would pull focus back into
  // the drawer — which is what stopped the finance correction-reason from being
  // typed (#481: pvdOpen was portalled but missing from this list). The inline
  // reject editor stays inside the drawer, so the trap keeps it. Call after these
  // states exist.
  useFocusTrap(drawerRef, !leaving && !reasonOpen && !authReasonOpen && !correcting && !pvdOpen);
  const pending = pendingCorrection(line);
  const canDecide = mayPlan && !!pending && pending.by !== meEmail;
  const milestoneName = (id?: string) => (id ? milestones.find((m) => m.id === id)?.name : undefined);
  const corrChange = (c: { field: 'pool' | 'phase' | 'milestone'; from: string; to: string }) => {
    if (c.field === 'pool') return `${c.from === 'capex' ? 'Capex' : 'Opex'} → ${c.to === 'capex' ? 'Capex' : 'Opex'}`;
    if (c.field === 'milestone') {
      const nm = (id: string) => (id ? milestoneName(id) ?? 'a milestone' : 'Not against a milestone');
      return `${nm(c.from)} → ${nm(c.to)}`;
    }
    const nm = (id: string) => (id ? phaseName(id) ?? 'Other' : 'Unassigned');
    return `${nm(c.from)} → ${nm(c.to)}`;
  };
  // "Billed against" — the billable milestones a line can claim against, and the
  // live claim this line makes against the one it names (milestone payments).
  const billableMilestones = milestones.filter((m) => m.billable);
  const billedMs = line.milestone ? milestones.find((m) => m.id === line.milestone) : undefined;
  const claim =
    billedMs && billedMs.billable && billedMs.billValue != null
      ? milestoneClaim(billedMs.id, billedMs.billValue, project?.invoices ?? [], line.id, line.amount)
      : null;
  const pickBilledAgainst = async (to: string) => {
    if (!project) return;
    const err = await setBilledAgainst(project.id, line.id, to);
    if (err) setProblem(err);
  };

  const approveCorr = async () => {
    if (!project || !pending) return;
    setProblem('');
    const err = await decideCorrection(project.id, line.id, pending.id, true);
    if (err) setProblem(err);
  };
  const rejectCorr = async () => {
    if (!project || !pending) return;
    const r = rejectReason.trim();
    if (!r) return;
    setProblem('');
    const err = await decideCorrection(project.id, line.id, pending.id, false, r);
    if (err) { setProblem(err); return; }
    setRejecting(false);
    setRejectReason('');
  };

  // While an in-drawer sub-overlay is open (the approve-with-reason modal, or the
  // inline reject-reason form), Escape closes only that — never the whole drawer,
  // which would discard the typed reason. Capture phase + stopPropagation beats
  // the drawer's own window Escape (app-wide nested-overlay rule, as in CalendarPopover).
  useEffect(() => {
    if (!reasonOpen && !authReasonOpen && !rejecting) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (reasonOpen) setReasonOpen(false);
      else if (authReasonOpen) setAuthReasonOpen(false);
      else { setRejecting(false); setRejectReason(''); }
    };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
  }, [reasonOpen, authReasonOpen, rejecting]);

  if (!project) return null;

  return (
    <aside
      ref={drawerRef}
      tabIndex={-1}
      className={`inv-drawer ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={`${line.vendor || 'Invoice line'} — invoice line`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="drw-head">
        <div className="lcd-headl">
          <span className="id-title">{line.vendor || 'Untitled line'}</span>
          {context && <span className="lcd-context">{context}</span>}
        </div>
        <div className="id-head-right">
          <span className="id-amt tnum">{faceMoney(line.amount)}</span>
          <StatusPill status={line.status} />
          <button className="drw-close" onClick={onClose} title="Close (Escape)" aria-label="Close (Escape)">
            <Icon name="close" size={17} />
          </button>
        </div>
      </div>

      <div className="drw-body id-body">
        <div className="id-meta">
          <span className={`bud-tag ${line.pool}`}>{line.pool === 'capex' ? 'Capex' : 'Opex'}</span>
          {line.phase && <span className="phase-chip"><span className="phase-chip-dot" />{phaseName(line.phase) ?? 'Other'}</span>}
          {settled ? (
            <span className="lcd-ro"><Icon name="lock" size={13} /> Settled — no further action required</span>
          ) : line.receivedAt ? (
            <span className="id-meta-date">Invoice received {fmtWhen(line.receivedAt)}</span>
          ) : (
            <span className="id-meta-date">Committed{line.date ? ` · ${fmtDay(line.date)}` : ''}</span>
          )}
        </div>

        {dupFlagged && !settled && (
          <div className="dup">
            <span className="dup-ic"><Icon name="warning" size={15} /></span>
            <span className="dup-t">
              Looks like a <b>possible duplicate</b> — another line has the same reference, vendor and amount.
            </span>
          </div>
        )}

        {/* RETENTION CEILING — paying this line would leave nothing withheld. */}
        {retentionBlocked && (
          <div className="dup ret-block">
            <span className="dup-ic"><Icon name="lock" size={15} /></span>
            <span className="dup-t">
              <b>Retention holds this back.</b> Paying {faceMoney(line.amount)} takes total paid past the{' '}
              {fmtMoney(ceiling.payCeiling)} ceiling — {ceiling.rate}% ({fmtMoney(ceiling.retentionAmount)}) of the{' '}
              {fmtMoney(ceiling.budget)} budget must stay withheld. That is {fmtMoney(payBlock.over)} too much to
              pay now.
            </span>
          </div>
        )}

        <div className="id-sec">
          <div className="id-h">{settled ? 'The gate, cleared' : 'Where this line stands'}</div>
          <div className="id-stepwrap"><Stepper doneUpTo={idx} /></div>
        </div>

        {/* THE PAYMENT-APPROVAL CHAIN — who authorises this payment, stage by stage,
            and who it waits on (executive-approver tier). Shown while the line is
            being authorised; a configured-but-unusable chain shows the blocked
            first-run route instead of a silent dead end. */}
        {chainGoverns && line.status === 'accepted' && (
          chainUsable ? (
            <PaymentChainPanel view={chainView} lead={chainLead} lineBase={lineBase} />
          ) : (
            <div className="id-sec">
              <div className="id-h">Authorisation chain</div>
              <div className="nochain">
                <span className="nochain-ic"><Icon name="shield" size={24} /></span>
                <h3 className="nochain-h">No payment approver is set</h3>
                <p className="nochain-sub">
                  No payment can be authorised until someone says who signs it off. FlightDeck never falls
                  back to &ldquo;any admin&rdquo;, so this line stays blocked until a chain is set.
                </p>
                {maySetApprovers && (
                  <button className="btn btn-primary" onClick={goToApprovalSetup}>
                    <Icon name="shield" size={14} /> Set who approves
                  </button>
                )}
                <span className="hint">
                  {maySetApprovers ? 'Opens Payment approval.' : 'An admin sets who approves payments.'}
                </span>
              </div>
            </div>
          )
        )}

        {/* ACCEPT ≠ APPROVE — a heads-up BEFORE the wall (#483 item 3 / #482's
            discoverability follow-up): the person who verified the work can't also
            approve its payment, so say so while the drawer is open rather than only
            at the block. The chain draws its own SoD seats, so this legacy note is
            for single-approver boards only. */}
        {!chainGoverns && line.status === 'accepted' && line.acceptedBy === meEmail && (
          <div className="id-sec">
            <div className="id-heads">
              <Icon name="info" size={14} />
              <span>
                {auth?.sodOverride ? (
                  <><b>You verified this work.</b> Approving its payment crosses separation of duties — you can still approve it as an admin, but it&rsquo;s recorded as a control exception and you&rsquo;ll be asked for a reason.</>
                ) : (
                  <><b>You verified this work,</b> so you can&rsquo;t also approve its payment. The designated approver must approve it — or an admin standing in, recorded as a control exception.</>
                )}
              </span>
            </div>
          </div>
        )}

        {mayPlan && (
          <div className="id-sec">
            <div className="id-h">Classification</div>
            <div className="cor-current">
              <span className={`bud-tag ${line.pool}`}>{line.pool === 'capex' ? 'Capex' : 'Opex'}</span>
              <span className="cor-phase">{line.phase ? (phaseName(line.phase) ?? 'Other') : 'Unassigned'}</span>
            </div>

            {/* BILLED AGAINST — a milestone picker mirroring the phase picker. A
                line need not claim a milestone (overheads, expenses); "Not against
                a milestone" is always available. Free while the line is unpaid;
                once paid, re-linking is a governed correction (Reclassify). */}
            {billableMilestones.length > 0 && (
              <div className="cor-billed">
                <span className="cor-billed-l">Billed against</span>
                {settled ? (
                  <span className="cor-billed-read">{billedMs ? billedMs.name : 'Not against a milestone'}</span>
                ) : (
                  <select
                    className="input"
                    aria-label="Billed against a milestone"
                    value={line.milestone ?? ''}
                    onChange={(e) => void pickBilledAgainst(e.target.value)}
                  >
                    <option value="">Not against a milestone</option>
                    {billableMilestones.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.billValue != null ? ` — ${fmtMoney(m.billValue)}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* DELIVERY BASIS — what "delivered" is measured against (#483): a phase,
                an epic, or a milestone. One picker, mirroring the billed-against one;
                editable until the line is accepted (the evidence freezes then). */}
            <div className="cor-billed">
              <span className="cor-billed-l">Delivery tracked against</span>
              {line.acceptedAt != null ? (
                <span className="cor-billed-read">{basisReadLabel}</span>
              ) : (
                <select
                  className="input"
                  aria-label="What this line&rsquo;s delivery is measured against"
                  value={curBasisValue}
                  onChange={(e) => void pickDeliveryBasis(e.target.value)}
                >
                  <option value="">Not tracked</option>
                  {livePhases.length > 0 && (
                    <optgroup label="Phase">
                      {livePhases.map((p) => (
                        <option key={`bph-${p.id}`} value={`phase::${p.id}`}>{p.name}</option>
                      ))}
                    </optgroup>
                  )}
                  {epicList.length > 0 && (
                    <optgroup label="Epic">
                      {epicList.map((e) => (
                        <option key={`bep-${e}`} value={`epic::${e}`}>{e}</option>
                      ))}
                    </optgroup>
                  )}
                  {milestones.length > 0 && (
                    <optgroup label="Milestone">
                      {milestones.map((m) => (
                        <option key={`bms-${m.id}`} value={`milestone::${m.id}`}>{m.name}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              )}
            </div>
            {!line.acceptedAt && (
              <p className="id-note-quiet">What &ldquo;delivered&rdquo; means for this line when it&rsquo;s accepted — the frozen evidence above. Defaults to the line&rsquo;s phase.</p>
            )}

            {/* THE CLAIM OF VALUE — this line's amount against the stage's value.
                The amount is the line's own Amount (free), so a part-claim/progress
                bill is natural; the % is a helper, never forced. Over-claim is a
                soft amber warning, never a block. */}
            {claim && billedMs && (() => {
              const sc = claim.scheduled || 1;
              const paidW = Math.max(0, Math.min(100, (claim.paidByOthers / sc) * 100));
              const alreadyW = Math.max(0, Math.min(100 - paidW, ((claim.claimedByOthers - claim.paidByOthers) / sc) * 100));
              const thisW = Math.max(0, Math.min(100 - paidW - alreadyW, (claim.thisClaim / sc) * 100));
              return (
                <div className={`cor-claim ${claim.over ? 'is-over' : ''}`}>
                  <div className="cor-claim-bar" aria-hidden="true">
                    <span className="cor-claim-paid" style={{ width: `${paidW}%` }} />
                    <span className="cor-claim-already" style={{ width: `${alreadyW}%` }} />
                    <span className="cor-claim-this" style={{ width: `${thisW}%` }} />
                  </div>
                  <p className="cor-claim-line">
                    Claiming <b className="tnum">{fmtMoney(claim.thisClaim)}</b> of{' '}
                    <b className="tnum">{fmtMoney(claim.scheduled)}</b>{' '}
                    <span className="cor-claim-pct">({Math.round(claim.pct)}%)</span> for <b>{billedMs.name}</b> &mdash;{' '}
                    <b className="tnum cor-claim-left">
                      {claim.leftToClaim >= 0
                        ? `${fmtMoney(claim.leftToClaim)} left to claim`
                        : `${fmtMoney(Math.abs(claim.leftToClaim))} over the stage`}
                    </b>.
                  </p>
                  <div className="cor-claim-legend">
                    <span><span className="cor-claim-key k-paid" /> Paid</span>
                    <span><span className="cor-claim-key k-already" /> Already claimed</span>
                    <span><span className="cor-claim-key k-this" /> This claim</span>
                  </div>
                  {claim.over && (
                    <p className="cor-claim-warn">
                      <Icon name="warning" size={14} /> This claim takes the stage past its {fmtMoney(claim.scheduled)} value. It can still be sent — just check it is intended.
                    </p>
                  )}
                </div>
              );
            })()}
            {pending ? (
              <div className="cor-pending">
                <div className="cor-pending-h"><Icon name="clock" size={13} /> <b>Reclassification awaiting approval</b></div>
                <div className="cor-pending-b">
                  {pending.field === 'pool' ? 'Pool' : pending.field === 'milestone' ? 'Billed against' : 'Phase'}: {corrChange(pending)} — <i>{pending.reason}</i>
                  <div className="cor-pending-who">Raised by {displayName(pending.by, profiles)} · {fmtWhen(pending.at)}</div>
                </div>
                {canDecide ? (
                  rejecting ? (
                    <div className="cor-reject">
                      <textarea className="input" rows={2} autoFocus placeholder="Why it’s rejected"
                        value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                      <div className="cor-reject-btns">
                        <button className="btn btn-small" onClick={() => { setRejecting(false); setRejectReason(''); }}>Cancel</button>
                        <button disabled={!rejectReason.trim()} {...inert('btn btn-primary btn-small', () => void rejectCorr())}>Reject</button>
                      </div>
                    </div>
                  ) : (
                    <div className="cor-decide">
                      <button {...inert('btn btn-small', () => setRejecting(true))}>Reject</button>
                      <button {...inert('btn btn-primary btn-small', () => void approveCorr())}>Approve</button>
                    </div>
                  )
                ) : (
                  <div className="cor-pending-note">
                    {pending.by === meEmail
                      ? 'You raised this — an admin or manager who did not raise it must decide it.'
                      : 'Waiting for an admin or manager to decide.'}
                  </div>
                )}
              </div>
            ) : (
              <button {...inert('btn btn-small cor-req-btn', () => setCorrecting(true))}>
                Reclassify
              </button>
            )}
          </div>
        )}

        {!settled && (
          <div className="id-sec">
            <div className="id-h">Invoice</div>
            {line.invoiceFile ? (
              <div className="doc-chip">
                <span className="doc-chip-ic"><Icon name="document" size={18} /></span>
                <span className="doc-chip-name">{line.invoiceFile.name}</span>
                <span className="doc-chip-meta">
                  {Math.max(1, Math.round(line.invoiceFile.size / 1024))} KB · added {fmtWhen(line.invoiceFile.addedAt)}
                </span>
                {mayPlan && (
                  <button {...inert('doc-chip-act', () => fileRef.current?.click())}>Replace</button>
                )}
              </div>
            ) : (
              <div className="doc-attach doc-missing">
                <span className="doc-missing-ic"><Icon name="warning" size={18} /></span>
                <div className="doc-missing-t">
                  <b>No invoice attached.</b> Upload the supplier’s invoice — the record of what is owed — before
                  this line is accepted and paid.
                </div>
                {mayPlan && (
                  <button {...inert('doc-chip-act', () => fileRef.current?.click())}>Attach the supplier invoice (PDF)</button>
                )}
              </div>
            )}
            <input ref={fileRef} type="file" accept="application/pdf" hidden disabled={sample.readOnly} onChange={onPickFile} />

            <div className="af-grid">
              <label className="af-cell">
                <span className="af-l">Amount<span className="req">*</span></span>
                {mayPlan && !financialsLocked ? (
                  <input className="input tnum" inputMode="numeric" aria-required value={amount}
                    onChange={(e) => setAmount(e.target.value)} onBlur={commitAmount} />
                ) : (
                  <span className="af-v tnum">{faceMoney(line.amount)}</span>
                )}
              </label>
              <label className="af-cell">
                <span className="af-l">Vendor<span className="req">*</span></span>
                {mayPlan && !financialsLocked ? (
                  <input className="input" aria-required value={vendor} onChange={(e) => setVendor(e.target.value)}
                    onBlur={() => { const v = vendor.trim(); if (v && v !== (line.vendor ?? '')) commit({ vendor: v }); }} />
                ) : (
                  <span className="af-v">{line.vendor || '—'}</span>
                )}
              </label>
              <label className="af-cell">
                <span className="af-l">Invoice date</span>
                {mayPlan ? (
                  <DateField ariaLabel="Invoice date" value={invDate} variant="form"
                    onChange={(v) => { setInvDate(v); commit({ date: v.trim() || undefined }); }} />
                ) : (
                  <span className="af-v">{fmtDay(line.date) || '—'}</span>
                )}
              </label>
              <label className="af-cell">
                <span className="af-l">Invoice ref</span>
                {mayPlan ? (
                  <input className="input" value={invRef} onChange={(e) => setInvRef(e.target.value)}
                    onBlur={() => { const v = invRef.trim(); if (v !== (line.invoiceRef ?? '')) commit({ invoiceRef: v || undefined }); }} />
                ) : (
                  <span className="af-v">{line.invoiceRef || '—'}</span>
                )}
              </label>
              <label className="af-cell">
                <span className="af-l">Payment due</span>
                {mayPlan ? (
                  <DateField ariaLabel="Payment due" value={dueDate} variant="form"
                    onChange={(v) => { setDueDate(v); commit({ dueDate: v.trim() || undefined }); }} />
                ) : (
                  <span className="af-v">{fmtDay(line.dueDate) || '—'}</span>
                )}
              </label>
            </div>

            {financialsLocked && mayPlan && (
              <div className="id-locknote">
                <Icon name="lock" size={13} />
                <span>The amount, vendor, currency and exchange rate are locked by the approval — move this line back to committed to change them. The invoice date and reference you can still correct.</span>
              </div>
            )}

            {poVariance && (
              <div className="match warn">
                <Icon name="warning" size={15} />
                <span>
                  <b>PO vs invoice:</b> {line.poNumber} was raised at <b>{faceMoney(poVariance.po)}</b>; this invoice
                  reads <b>{faceMoney(poVariance.inv)}</b> — <b>{faceMoney(Math.abs(poVariance.inv - poVariance.po))}{' '}
                  {poVariance.over ? 'over' : 'under'}</b>. Worth a look before you accept.
                </span>
              </div>
            )}
          </div>
        )}

        {!settled && evidence && (() => {
          const snap = evidence.snap;
          const label = snap.basisLabel ?? snap.phaseName;
          const word = snap.basisKind === 'epic' ? 'epic' : snap.basisKind === 'milestone' ? 'milestone' : 'phase';
          const isMsUnit = snap.unit === 'milestone';
          const reached = snap.delivered > 0;
          return (
          <div className="id-sec">
            <div className="id-h">Delivery evidence{label ? ` — from the ${label} ${word}` : ''}</div>
            {isMsUnit ? (
              <>
                <div className="acc-evid-head">
                  {label && <span className="phase-chip"><span className={`phase-chip-dot ${reached ? 'phase-done' : ''}`} />{label}</span>}
                  <span className="acc-evid-big">{reached ? 'Milestone reached' : 'Not yet reached'}</span>
                </div>
                <p className="acc-frozen">
                  <Icon name="lock" size={13} />
                  {evidence.frozen ? (
                    <span>Snapshotted onto the acceptance as at <b className="nowrap">{fmtWhen(snap.at)}</b> — it won&rsquo;t re-check if the milestone moves later.</span>
                  ) : (
                    <span>Captured onto the line when you accept it — it won&rsquo;t re-check if the milestone moves afterwards.</span>
                  )}
                </p>
              </>
            ) : snap.total > 0 ? (
              <>
                <div className="acc-evid-head">
                  {label && (
                    <span className="phase-chip"><span className="phase-chip-dot phase-done" />{label}</span>
                  )}
                  <span className="acc-evid-big">{snap.done} of {snap.total} cards done</span>
                </div>
                <div className="split">
                  {Array.from({ length: snap.delivered }).map((_, i) => (
                    <span key={`d${i}`} className="split-seg delivered" style={{ flex: 1 }} />
                  ))}
                  {Array.from({ length: snap.dropped }).map((_, i) => (
                    <span key={`x${i}`} className="split-seg dropped" style={{ flex: 1 }} />
                  ))}
                </div>
                <div className="split-legend">
                  <span><i className="delivered" />{snap.delivered} delivered</span>
                  <span><i className="dropped" />{snap.dropped} dropped</span>
                </div>
                <p className="acc-frozen">
                  <Icon name="lock" size={13} />
                  {evidence.frozen ? (
                    <span>Snapshotted onto the acceptance as at <b className="nowrap">{fmtWhen(snap.at)}</b> — it won&rsquo;t re-count if the board moves later.</span>
                  ) : (
                    <span>Captured onto the line when you accept it — it won&rsquo;t re-count if the board moves afterwards.</span>
                  )}
                </p>
              </>
            ) : (
              <p className="acc-plain">No cards in this {word} yet — you&rsquo;re verifying the work itself.</p>
            )}
            {nudgeWarns && deliveryNudge && (
              <p className="id-nudge">
                <Icon name="warning" size={14} />
                <span>
                  This pays for work that is <b>{pctOf(deliveredNow)} delivered</b> — below your board&rsquo;s{' '}
                  <b>{deliveryNudge.pct}%</b> bar. You can still approve it; the shortfall will be recorded on the line.
                </span>
              </p>
            )}
          </div>
          );
        })()}

        {/* THE WHOLE-CHAIN STAMP (surface 3c) — once every required stage has signed,
            the line is authorised and reads the FULL chain in order, not one approver. */}
        {chainRaised && isApproved && !settled && (
          <div className="id-sec">
            <div className="id-h">Payment authorised</div>
            <div className="paystamp">
              <div className="paystamp-mark"><Icon name="check" size={16} /></div>
              <div className="paystamp-body">
                <div className="paystamp-ref">Authorised to pay · {fmtWhen(line.approvedAt)}</div>
                <div className="paystamp-line">Cleared through every required stage. Now in <b>Ready to Pay</b> for finance to settle.</div>
                <div className="paystamp-chain">
                  {chainView.stages.filter((s) => s.key !== 'authorised').map((s) => {
                    const who = s.seats.filter((x) => x.state === 'ok').map((x) => x.main).filter(Boolean).join(' + ');
                    return <div key={s.key}><b>{s.name}</b>{who ? <> — {who}</> : null}</div>;
                  })}
                </div>
                {chainView.band && (
                  <div className="paystamp-bound"><Icon name="coin" size={12} /> Band applied: ≥ {fmtMoney(chainView.band.amount)}</div>
                )}
              </div>
            </div>
            <div className="paystamp-acts">
              <button className="btn btn-small" disabled={makingAuthority} onClick={viewAuthority}>
                <Icon name="document" size={14} /> {makingAuthority ? 'Preparing…' : 'View authority to pay'}
              </button>
              {line.invoiceFile && (
                <button className="btn btn-small" onClick={viewInvoice}><Icon name="document" size={14} /> View invoice</button>
              )}
            </div>
          </div>
        )}

        {isApproved && !settled && !chainRaised && (
          <div className="id-sec">
            <div className="id-h">Authority to pay</div>
            <div className={`paystamp${stampState === 'voided' ? ' voided' : ''}`}>
              <div className="paystamp-mark">
                <Icon name={stampState === 'voided' ? 'warning' : 'check'} size={16} />
              </div>
              <div className="paystamp-body">
                <div className="paystamp-ref">{stampRef}</div>
                <div className="paystamp-line">
                  {stampKind === 'cover' && line.approvalCoverFor ? (
                    <>Authorised by <b>{displayName(line.approvedBy ?? '', profiles)}</b> as delegated cover for <b>{displayName(line.approvalCoverFor, profiles)}</b></>
                  ) : stampKind === 'exception' ? (
                    <>Authorised by <b>{displayName(line.approvedBy ?? '', profiles)}</b> in the approver&rsquo;s place — <span className="paystamp-exc">control exception</span></>
                  ) : (
                    <>Authorised by <b>{displayName(line.approvedBy ?? '', profiles)}</b></>
                  )}
                  {' '}on {fmtWhen(line.approvedAt)}
                </div>
                <div className="paystamp-bound">
                  {stampState === 'voided' ? (
                    <><Icon name="warning" size={12} /> The invoice changed — this authority no longer binds. Approve the line again.</>
                  ) : line.invoiceFile?.hash ? (
                    <><Icon name="lock" size={12} /> Bound to {line.invoiceFile.name}</>
                  ) : (
                    <><Icon name="document" size={12} /> {line.invoiceFile?.name ?? 'the supplier invoice'}</>
                  )}
                </div>
                {belowNote && (
                  <div className="paystamp-below">
                    <Icon name="warning" size={12} /> Approved at <b>{pctOf(belowNote.share)}</b> delivered — below the board&rsquo;s <b>{belowNote.pct}%</b> bar at the time.{belowNote.reason ? <> &ldquo;{belowNote.reason}&rdquo;</> : null}
                  </div>
                )}
              </div>
            </div>
            <div className="paystamp-acts">
              <button className="btn btn-small" disabled={makingAuthority} onClick={viewAuthority}>
                <Icon name="document" size={14} /> {makingAuthority ? 'Preparing…' : 'View authority to pay'}
              </button>
              {line.invoiceFile && (
                <button className="btn btn-small" onClick={viewInvoice}><Icon name="document" size={14} /> View invoice</button>
              )}
            </div>
            <p className="id-note-quiet">“View authority to pay” opens the invoice stamped with who authorised it — bound to this document by its fingerprint. The stored original is left untouched; replacing the invoice voids the authority and the line must be approved again.</p>
          </div>
        )}

        {settled && (
          <div className="id-sec">
            <div className="id-h">How it was paid</div>
            <div className="prov-block">
              {line.receivedAt && (
                <div className="prov-row"><span className="prov-check"><Icon name="check" size={11} /></span>
                  <span>Received <b>{fmtWhen(line.receivedAt)}</b>{line.invoiceRef ? ` — invoice ${line.invoiceRef}` : ''}</span></div>
              )}
              {line.acceptedAt && (
                <div className="prov-row"><span className="prov-check"><Icon name="check" size={11} /></span>
                  <span>Accepted by <b>{displayName(line.acceptedBy ?? '', profiles)}</b> on {fmtWhen(line.acceptedAt)} — work verified</span></div>
              )}
              {line.approvedAt && (
                <div className="prov-row"><span className="prov-check"><Icon name="check" size={11} /></span>
                  <span>
                    Approved by <b>{displayName(line.approvedBy ?? '', profiles)}</b> on {fmtWhen(line.approvedAt)}
                    {line.approvalBasis === 'cover' && line.approvalCoverFor
                      ? <> — as delegated cover for <b>{displayName(line.approvalCoverFor, profiles)}</b></>
                      : line.sodOverride
                      ? <> — overriding separation of duties</>
                      : line.approvalBasis === 'admin' && line.approvalReason
                      ? <> — in the approver&rsquo;s place</>
                      : null}
                  </span></div>
              )}
              {belowNote && (
                <div className="prov-row prov-await"><span className="prov-check"><Icon name="warning" size={11} /></span>
                  <span>Approved at <b>{pctOf(belowNote.share)}</b> delivered — below the board&rsquo;s <b>{belowNote.pct}%</b> bar at the time{belowNote.reason ? <> — &ldquo;{belowNote.reason}&rdquo;</> : null}</span></div>
              )}
              {line.paidAt && (
                <div className="prov-row"><span className="prov-check"><Icon name="check" size={11} /></span>
                  <span>Marked paid in FlightDeck <b>{fmtWhen(line.paidAt)}</b> · {faceMoney(line.amount)}</span></div>
              )}
              {line.paidAt && (line.paymentValueDate ? (
                <div className="prov-row"><span className="prov-check"><Icon name="check" size={11} /></span>
                  <span>Finance recorded the payment on <b>{fmtDay(line.paymentValueDate)}</b></span></div>
              ) : (
                <div className="prov-row prov-await"><span className="prov-check"><Icon name="clock" size={11} /></span>
                  <span>Awaiting the finance payment date</span></div>
              ))}
            </div>
            {(() => {
              const log = line.paymentValueDateLog ?? [];
              const last = log[log.length - 1];
              if (!last || !last.from) return null;
              return (
                <div className="sod">
                  <Icon name="info" size={13} />
                  <span>Finance payment date corrected from <b>{fmtDay(last.from)}</b> by <b>{displayName(last.by, profiles)}</b> on {fmtWhen(last.at)}{last.reason ? <> — <i>{last.reason}</i></> : null}</span>
                </div>
              );
            })()}
            {(line.sodOverride || (line.approvalBasis === 'admin' && line.approvalReason)) && (
              <div className="sod">
                <Icon name="info" size={13} />
                <span>
                  {line.sodOverride ? (
                    <><b>Control exception.</b> Approved by the same person who verified the work, as a separation-of-duties override — <i>{line.approvalReason}</i></>
                  ) : (
                    <><b>Control exception.</b> Approved in the approver&rsquo;s place — <i>{line.approvalReason}</i></>
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        {settled && mayPlan && (
          <div className="id-sec">
            <div className="id-h">Invoice details</div>
            <div className="af-grid">
              <label className="af-cell">
                <span className="af-l">Invoice date</span>
                <DateField ariaLabel="Invoice date" value={invDate} variant="form"
                  onChange={(v) => { setInvDate(v); commit({ date: v.trim() || undefined }); }} />
              </label>
              <label className="af-cell">
                <span className="af-l">Invoice ref</span>
                <input className="input" value={invRef} onChange={(e) => setInvRef(e.target.value)}
                  onBlur={() => { const v = invRef.trim(); if (v !== (line.invoiceRef ?? '')) commit({ invoiceRef: v || undefined }); }} />
              </label>
              <label className="af-cell">
                <span className="af-l">Payment due</span>
                <DateField ariaLabel="Payment due" value={dueDate} variant="form"
                  onChange={(v) => { setDueDate(v); commit({ dueDate: v.trim() || undefined }); }} />
              </label>
            </div>
            <p className="id-note-quiet">The date, reference and payment due date stay editable; the amount and vendor are locked now this line is paid.</p>
            <div className="pvd-ctl">
              <span className="af-l">Payment date (per finance)</span>
              <span className="pvd-val">{line.paymentValueDate ? fmtDay(line.paymentValueDate) : 'Not set'}</span>
              <button className="btn btn-small" onClick={() => setPvdOpen(true)}>
                {line.paymentValueDate ? 'Correct' : 'Set the finance date'}
              </button>
            </div>
            <p className="id-note-quiet">The paid date from your finance system, kept separate from when it was marked paid here. It can’t be before the approval; a future date is allowed but flagged.</p>
          </div>
        )}

        {settled && line.poNumber && line.poAmount != null && (
          <div className="id-sec">
            <div className="id-h">PO vs invoice</div>
            {line.poAmount === line.amount ? (
              <div className="match ok">
                <Icon name="check" size={14} />
                <span><b>Matched.</b> {line.poNumber}{line.invoiceRef ? ` and invoice ${line.invoiceRef}` : ''} both read <b>{faceMoney(line.amount)}</b> — no variance.</span>
              </div>
            ) : (
              <div className="match warn">
                <Icon name="warning" size={15} />
                <span><b>{line.poNumber}</b> was raised at <b>{faceMoney(line.poAmount)}</b>; the invoice read <b>{faceMoney(line.amount)}</b>.</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="drw-foot">
        {problem && <p className="id-problem">{problem}</p>}
        {settled ? (
          <>
            <span className="id-foot-note">Paid {fmtWhen(line.paidAt)} · in {base} (base)</span>
            <span className="id-foot-btns">
              {isApproved && (
                <button className="btn btn-small" disabled={makingAuthority} onClick={viewAuthority}>
                  <Icon name="document" size={14} /> {makingAuthority ? 'Preparing…' : 'View authority to pay'}
                </button>
              )}
              {line.invoiceFile && (
                <button className="btn btn-small" onClick={viewInvoice}><Icon name="document" size={14} /> View invoice</button>
              )}
              <button className="btn btn-small" onClick={onClose}>Close</button>
            </span>
          </>
        ) : (
          <>
            <span className="id-foot-note">
              {retentionBlocked ? (
                <>Retention is holding this line back — see above.</>
              ) : line.status === 'approved' ? (
                <>Accepted and approved — this line is cleared to pay.</>
              ) : line.status === 'accepted' && chainGoverns ? (
                !hasInvoice ? (
                  <>Attach the supplier invoice before this line can be sent for authorisation.</>
                ) : !chainUsable ? (
                  <>Blocked — no one can authorise this payment yet. <b>Set who approves</b> to continue.</>
                ) : chainAuth?.allowed ? (
                  chainAuth.sodOverride ? (
                    <>You verified this work — authorising here is recorded as a control exception, with a reason.</>
                  ) : (
                    <>You&rsquo;re a required signer of the stage open now. Authorise it to move the payment on.</>
                  )
                ) : !chainRaised ? (
                  <>Ready to send for authorisation — the chain above is asked in order.</>
                ) : iAcceptedThis ? (
                  <>You verified this work, so you can&rsquo;t authorise its payment — it goes to the other approvers.</>
                ) : chainView.waitsOn ? (
                  <>{chainView.waitsOn.stageName} — <b>{chainView.waitsOn.signed} of {chainView.waitsOn.need} signed</b>.{chainView.waitsOn.names.length ? <> Next: <b>{chainView.waitsOn.names[0]}</b>.</> : null}</>
                ) : (
                  <>Waiting on the next approver.</>
                )
              ) : line.status === 'accepted' ? (
                !hasInvoice ? (
                  <>Attach the supplier invoice before this line can be approved.</>
                ) : auth?.allowed ? (
                  auth.sodOverride ? (
                    <>You verified this work — approving it here is recorded as a control exception, with a reason.</>
                  ) : auth.needsReason ? (
                    <>You&rsquo;re approving in the approver&rsquo;s place — a reason will be recorded.</>
                  ) : auth.basis === 'cover' ? (
                    <>You&rsquo;re approving as cover for <b>{displayName(auth.coverFor ?? '', profiles)}</b>.</>
                  ) : (
                    <>Ready to approve this line for payment.</>
                  )
                ) : (
                  <>
                    {auth?.blockedWhy}
                    {line.approvalRequestedAt && approver ? <> Sent to <b>{displayName(approver, profiles)}</b> for approval.</> : null}
                  </>
                )
              ) : (
                <>Next step: a person verifies the work. <b>Mark paid</b> unlocks only after it is accepted <b>and</b> approved.</>
              )}
              {mayPlan && (
                confirmRemove ? (
                  <button {...inert('bud-del confirm', () => void remove())}>Remove this line?</button>
                ) : (
                  <button {...inert('bud-del', () => setConfirmRemove(true))}>Remove line</button>
                )
              )}
            </span>
            <span className="id-foot-btns">
              {chainGoverns && line.status === 'accepted' ? (
                !hasInvoice ? (
                  <>
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Invoice required</span>
                    <button className="btn btn-primary btn-small" disabled>Authorise payment</button>
                  </>
                ) : !chainUsable ? (
                  <>
                    {maySetApprovers && (
                      <button className="btn btn-small" onClick={goToApprovalSetup}>
                        <Icon name="shield" size={14} /> Set who approves
                      </button>
                    )}
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Approve locked</span>
                    <button className="btn btn-primary btn-small" disabled>Authorise payment</button>
                  </>
                ) : chainAuth?.allowed ? (
                  <button {...inert('btn btn-primary btn-small', doAuthorise)}>
                    <Icon name="shield" size={14} /> {chainAuth.sodOverride ? 'Override, with a reason…' : 'Authorise payment'}
                  </button>
                ) : !chainRaised ? (
                  mayPlan ? (
                    <button {...inert('btn btn-primary btn-small', () => void act(() => raisePaymentChain(project!.id, line.id)))}>
                      <Icon name="mail" size={14} /> Send for authorisation
                    </button>
                  ) : (
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Awaiting authorisation</span>
                  )
                ) : iAcceptedThis ? (
                  <>
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Separation of duties</span>
                    <button className="btn btn-primary btn-small" disabled>Authorise payment</button>
                  </>
                ) : mayPlan && chainView.waitsOn && chainView.waitsOn.names.length > 0 ? (
                  <button {...inert('btn btn-small remind', () => void act(() => remindPaymentChain(project!.id, line.id)))}>
                    <Icon name="mail" size={14} /> Remind {chainView.waitsOn.names[0]}
                  </button>
                ) : (
                  <span className="id-foot-lock"><Icon name="lock" size={13} /> Awaiting authorisation</span>
                )
              ) : !mayPlan ? (
                <span className="id-foot-lock"><Icon name="lock" size={13} /> View only</span>
              ) : line.status === 'accepted' ? (
                !hasInvoice ? (
                  <>
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Invoice required</span>
                    <button className="btn btn-primary btn-small" disabled>Approve for payment</button>
                  </>
                ) : auth?.allowed ? (
                  <button {...inert('btn btn-primary btn-small', doApprove)}>
                    {auth.needsReason ? 'Approve with reason…' : 'Approve for payment'}
                  </button>
                ) : (
                  <>
                    {!line.approvalRequestedAt && !!approver && (
                      <button {...inert('btn btn-small', () => void act(() => requestPayApproval(project!.id, line.id)))}>
                        <Icon name="mail" size={14} /> Send to approver
                      </button>
                    )}
                    <button className="btn btn-primary btn-small" disabled>Approve for payment</button>
                  </>
                )
              ) : (
                <>
                  {line.status !== 'approved' && !canMarkPaid(line) && (
                    <span className="id-foot-lock"><Icon name="lock" size={13} /> Mark paid locked</span>
                  )}
                  {next && (
                    <button disabled={nextDisabled}
                      {...inert('btn btn-primary btn-small', () => void act(next.run))}>
                      {line.status === 'received' && <Icon name="check" size={14} />} {next.label}
                    </button>
                  )}
                </>
              )}
            </span>
          </>
        )}
      </div>
      {reasonOpen && createPortal(
        <div className="overlay" onClick={() => setReasonOpen(false)}>
          <div className="modal id-reason" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <span className="modal-head-title">
                {auth?.sodOverride ? 'Override separation of duties'
                  : auth?.needsReason ? 'Approve in the approver’s place'
                  : 'Approve ahead of delivery'}
              </span>
            </div>
            <div className="modal-body">
              <p className="id-reason-p">
                {auth?.sodOverride ? (
                  <>You verified this work, so approving its payment crosses separation of duties. As a manager or admin you may override it — the reason is recorded on the line as a control exception.</>
                ) : auth?.needsReason ? (
                  <>You&rsquo;re approving in place of <b>{displayName(approver, profiles)}</b>. Say why you&rsquo;re standing in — it&rsquo;s recorded on the line as a control exception.</>
                ) : (
                  <>This pays for work that is <b>{pctOf(deliveredNow)}</b> delivered — below your board&rsquo;s <b>{deliveryNudge?.pct}%</b> bar. Say why you&rsquo;re approving it ahead of delivery; it&rsquo;s recorded on the line.</>
                )}
              </p>
              <label className="af-cell">
                <span className="af-l">Reason<span className="req">*</span></span>
                <textarea className="input" rows={3} value={reasonText} autoFocus aria-required
                  onChange={(e) => setReasonText(e.target.value)} />
              </label>
            </div>
            <div className="modal-actions id-reason-btns">
              <button className="btn btn-small" onClick={() => setReasonOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-small" disabled={!reasonText.trim()} onClick={submitReason}>
                Approve for payment
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {authReasonOpen && createPortal(
        <div className="overlay" onClick={() => setAuthReasonOpen(false)}>
          <div className="modal id-reason" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <span className="modal-head-title">
                {chainAuth?.sodOverride ? 'Override separation of duties' : 'Authorise this payment'}
              </span>
            </div>
            <div className="modal-body">
              <p className="id-reason-p">
                {chainAuth?.sodOverride ? (
                  <>You verified this work, so authorising its payment crosses separation of duties. As a manager or admin you may override it — the reason is recorded on the line as a control exception.</>
                ) : (
                  <>You&rsquo;re authorising the <b>{chainView.waitsOn?.stageName}</b> stage. Say why you&rsquo;re standing in — it&rsquo;s recorded on the line as a control exception.</>
                )}
              </p>
              <label className="af-cell">
                <span className="af-l">Reason<span className="req">*</span></span>
                <textarea className="input" rows={3} value={authReasonText} autoFocus aria-required
                  onChange={(e) => setAuthReasonText(e.target.value)} />
              </label>
            </div>
            <div className="modal-actions id-reason-btns">
              <button className="btn btn-small" onClick={() => setAuthReasonOpen(false)}>Cancel</button>
              <button className="btn btn-primary btn-small" disabled={!authReasonText.trim()} onClick={submitAuthReason}>
                Authorise payment
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {correcting && project && (
        <CorrectionDialog projectId={project.id} line={line} onClose={() => setCorrecting(false)} />
      )}
      {pvdOpen && project && (
        <PaymentDateDialog
          line={line}
          onSave={(value, reason) => setPaymentValueDate(project.id, line.id, value, reason)}
          onClose={() => setPvdOpen(false)}
        />
      )}
    </aside>
  );
}
